'use client';

import { useEffect, useRef } from 'react';
import { arrivedViaHistoryTraversal } from '@/core/utils/historyTraversal';

// Last product-listing address + scroll position, so returning from a product lands where the
// shopper clicked.
const LISTING_SCROLL_KEY = 'shopstag-listing-scroll';

/**
 * Product listings (category and all-products pages): remembers the scroll position per address
 * and, when the shopper comes back with back/forward, restores it once the listing's products are
 * in. The browser's own restoration can't do it — the listing re-renders (a loading skeleton, a
 * client fetch of the page's products), so it clamps to a short page and then drifts (measured
 * 911 → 1191 px on /produkty). A regular link click starts at the top as before.
 *
 * `waitsForLoad()` — read on mount: whether the listing is about to fetch its products (a loading
 * true → false cycle follows); the position is restored after that load instead of immediately.
 */
export function useListingScrollRestore(enabled: boolean, loading: boolean, waitsForLoad: () => boolean = () => false) {
    const pending = useRef<{ y: number; waitForLoad: boolean; sawLoading: boolean } | null>(null);

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') return;
        const listingUrl = () => `${window.location.pathname}${window.location.search}`;
        try {
            const saved = JSON.parse(sessionStorage.getItem(LISTING_SCROLL_KEY) || '{}');
            if (saved.url === listingUrl() && typeof saved.y === 'number' && arrivedViaHistoryTraversal()) {
                pending.current = { y: saved.y, waitForLoad: waitsForLoad(), sawLoading: false };
            }
        } catch { /* no storage */ }

        let frame = 0;
        const remember = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                if (pending.current) return; // don't overwrite the saved position before restoring it
                try {
                    sessionStorage.setItem(LISTING_SCROLL_KEY, JSON.stringify({ url: listingUrl(), y: window.scrollY }));
                } catch { /* no storage */ }
            });
        };
        window.addEventListener('scroll', remember, { passive: true });
        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', remember);
        };
    }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const restore = pending.current;
        if (!restore) return;
        if (loading) {
            restore.sawLoading = true;
            return;
        }
        if (restore.waitForLoad && !restore.sawLoading) return;
        requestAnimationFrame(() => {
            window.scrollTo(0, restore.y);
            pending.current = null;
        });
    }, [loading]);
}
